import { useTranslation } from "react-i18next";

import { CoinPurse } from "../../components/game/CoinPurse.js";
import { PhaseTimer } from "../../components/game/PhaseTimer.js";
import { Vinyl } from "../../components/game/Vinyl.js";
import type { GameView } from "../../services/gameLink.js";

/** How big the disc is on the stage — the one fixed thing on this screen. */
const DISC = 128;

/** The viewer's coins, falling back to their side's when they have no slice. */
function mySideCoins(view: GameView): number {
  return (
    view.you?.coins ??
    view.board?.sides.find((side) => side.id === view.you?.side)?.coins ??
    0
  );
}

/**
 * One stage's frame: a heading nobody has to hunt for, the record, and content.
 *
 * The heading is `aria-live` because on a stepper the *stage changing* is the
 * event — a screen reader that only announced the button would never say the
 * round had moved on.
 *
 * # 🔴 The record is here, in every step, and it is the clock
 *
 * 20d's first pass had two discs: a countdown in the header and a decorative one
 * on stage while a clip played. The maintainer's call was to collapse them —
 * *"move the timer inside the body replacing the guessing place and always
 * showing the timer there"*. So this draws **one** disc, in one position, in
 * every phase, and what changes is only what it is doing:
 *
 * | | |
 * |---|---|
 * | a clip is playing | the highlight turns on the outside, the ring counts two rings in |
 * | anything else timed | the ring counts on the outside |
 * | nothing timed | a still record — a countdown towards nothing is the "why is it stuck?" a timer exists to answer |
 *
 * ⚠️ **This is what finding #3 now rests on** — *"the timer should always be
 * visible and at the top"*. It is no longer in chrome that cannot scroll; it is
 * at the top of the stage band, which *can* scroll when a stage overflows.
 * 20d's bands make that rare, and the disc is the first thing in the band. If a
 * party reports losing the clock again, the fix is to make this row sticky
 * within the band rather than to put a second one back in the header.
 */
export function StageBody({
  title,
  view,
  powers,
  children,
}: {
  title: string;
  view: GameView;
  /** The powers strip and its picker — see [`Powers`](../../components/game/Powers.js). */
  powers?: React.ReactNode;
  children: React.ReactNode;
}) {
  const { t } = useTranslation();
  const playing = view.board?.phase === "listening";

  return (
    <section data-testid="round-stage" className="flex min-w-0 flex-col gap-3">
      {/* The phase, and what it is costing you: the two labels that qualify the
          disc below them. ⚠️ The purse lives on this row rather than in the
          header because the header's instruction wraps, and a chip pinned to
          that corner sat *on top of* the sentence 3.1 exists to protect. A row
          pushes things around; an overlay covers them. */}
      {/* ⚠️ `items-center` and no nudge. This was `items-start` with a `-mt-1`
          on the purse — a hand-tuned offset, which is what a row looks like
          when two things of different heights are told to align by their tops
          and then dragged back into place. */}
      <div className="flex items-center justify-between gap-3">
        <h2
          aria-live="polite"
          className="text-xs font-semibold uppercase tracking-[0.2em] text-white/40"
        >
          {title}
        </h2>
        {/* `chip` is what marks this one as **yours**: a `SideBoard` header
            carries a purse too, and two identical chips on one screen say the
            same thing about two different people. */}
        <CoinPurse chip coins={mySideCoins(view)} className="shrink-0" />
      </div>

      <div className="flex justify-center">
        {view.phaseEndsAt === null ? (
          // Nothing is timing this phase, so there is nothing to count — but the
          // disc is the anchor this screen is built around now, so it stays, and
          // it says what it honestly knows: a clip is playing, or it is not.
          <Vinyl spinning={playing} size={DISC} />
        ) : (
          <PhaseTimer
            endsAt={view.phaseEndsAt}
            seconds={view.phaseSeconds}
            spinning={playing}
            size={DISC}
          />
        )}
      </div>

      {/* 🔴 **The pot, on every stage that has one** — phase 20h-4, ADR 0029.
          A tied Deathmatch round hands its card to nobody, and unless something
          says where it went the room reads that as the game losing a card
          rather than banking it. Above the stage's own content because it is a
          standing fact about the game rather than part of the question being
          asked, and it renders at all only when there is something in it. */}
      {(view.board?.pot ?? 0) > 0 && (
        <p
          role="status"
          className="rounded-lg border border-coin-400/40 bg-coin-400/10 px-3 py-2 text-center text-sm text-coin-400"
        >
          {t("round.pot", { count: view.board?.pot ?? 0 })}
        </p>
      )}

      {children}

      {/* 🔴 **Last, and on every stage** — ADR 0026. `available_purchases` is
          phase-correct by construction, so one render site is the whole of
          *"the shop becomes contextual"*: the strip is empty in the phases that
          sell nothing and carries steal between rounds, narrow while the grids
          are open, blind and insurance before the card is dealt.

          Under the question rather than over it: a power is what you *may* do,
          and the stage is what you are being asked. */}
      {powers}
    </section>
  );
}
